/**
 * invoice-recipient.ts -- fakturamottaker som avviker fra avtaleparten.
 *
 * SJELDEN, MEN REELL: kunden signerer selv (avtalepart), men ber om at
 * fakturaen stilles til et ANNET selskap som skal betale (typisk arrangoeren
 * som avvikler arrangementet). Avtaleparten hefter uansett -- vilkaarene gjoer
 * kontrahent og signerende part solidarisk ansvarlige -- saa fakturamottakeren
 * er KUN en betalingsadresse, aldri part i avtalen.
 *
 * Feltene ligger paa tilbudet (der kunden signerer) og paa ordren (der Fiken
 * henter fakturagrunnlaget). Tomme felt = dagens oppfoersel i alle ledd.
 *
 * ALT-ELLER-INGENTING: firmanavn og organisasjonsnummer maa vaere satt sammen.
 * Halvveis utfylt er en feil, ikke en delvis overstyring: uten orgnr kan vi
 * ikke slaa opp riktig Fiken-kontakt (og ville risikert aa treffe avtalepartens
 * kontakt via e-postsoeket), og uten firmanavn har fakturaen ingen mottaker.
 */

export interface InvoiceRecipientSource {
  invoiceRecipientCompany?: string | null;
  invoiceRecipientOrgNumber?: string | null;
}

export interface InvoiceRecipient {
  /** Firmanavnet fakturaen skal utstedes til (navnet paa Fiken-kontakten). */
  company: string;
  /** Kun sifre, alltid ni. */
  orgNumber: string;
}

export type InvoiceRecipientProblem =
  | "missing_company"
  | "missing_org_number"
  | "invalid_org_number";

export const INVOICE_RECIPIENT_PROBLEM_TEXT: Record<InvoiceRecipientProblem, string> = {
  missing_company: "Fakturamottaker mangler firmanavn (organisasjonsnummer er fylt ut)",
  missing_org_number: "Fakturamottaker mangler organisasjonsnummer (firmanavn er fylt ut)",
  invalid_org_number: "Fakturamottakerens organisasjonsnummer må ha ni siffer",
};

function clean(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** Kun sifrene: «925 137 529» og «NO925137529MVA» blir «925137529». */
export function normalizeOrgNumber(value: unknown): string {
  return String(value ?? "").replace(/\D/g, "");
}

export function isValidOrgNumber(value: unknown): boolean {
  return normalizeOrgNumber(value).length === 9;
}

/** «925137529» -> «925 137 529». Ugyldige verdier vises som skrevet. */
export function formatOrgNumber(value: unknown): string {
  const digits = normalizeOrgNumber(value);
  if (digits.length !== 9) return clean(value) || String(value ?? "").trim();
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

/**
 * null = feltene er enten tomme (normalflyten) eller komplette. Alt annet er
 * en halvveis utfylt fakturamottaker, som ALDRI skal gi en faktura.
 */
export function invoiceRecipientProblem(
  source: InvoiceRecipientSource | null | undefined,
): InvoiceRecipientProblem | null {
  const company = clean(source?.invoiceRecipientCompany);
  const orgNumber = clean(source?.invoiceRecipientOrgNumber);
  if (!company && !orgNumber) return null;
  if (!company) return "missing_company";
  if (!orgNumber) return "missing_org_number";
  if (!isValidOrgNumber(orgNumber)) return "invalid_org_number";
  return null;
}

/** Fakturamottakeren, eller null naar feltene er tomme/ufullstendige. */
export function resolveInvoiceRecipient(
  source: InvoiceRecipientSource | null | undefined,
): InvoiceRecipient | null {
  if (invoiceRecipientProblem(source)) return null;
  const company = clean(source?.invoiceRecipientCompany);
  const orgNumber = normalizeOrgNumber(source?.invoiceRecipientOrgNumber);
  if (!company || !orgNumber) return null;
  return { company, orgNumber };
}

/**
 * Fakturamottaker fra ordren, ellers fra tilbudet (samme «ordren vinner»-
 * fallback som invoiceReference/invoicePurchaseOrder har i kontraktvisningene).
 *
 * Feltene velges som PAR, aldri felt for felt: et firmanavn fra ordren og et
 * organisasjonsnummer fra tilbudet ville gitt en mottaker som ikke finnes
 * noen av stedene. Har ordren saa mye som ett av feltene utfylt, er det
 * ordren som gjelder -- da er tilbudets verdi bevisst overstyrt.
 */
export function mergeInvoiceRecipientSource(
  order: InvoiceRecipientSource | null | undefined,
  quote: InvoiceRecipientSource | null | undefined,
): InvoiceRecipientSource {
  const orderHasValue = Boolean(
    clean(order?.invoiceRecipientCompany) || clean(order?.invoiceRecipientOrgNumber),
  );
  const source = orderHasValue ? order : quote;
  return {
    invoiceRecipientCompany: source?.invoiceRecipientCompany ?? null,
    invoiceRecipientOrgNumber: source?.invoiceRecipientOrgNumber ?? null,
  };
}

export interface InvoiceRecipientContractInput extends InvoiceRecipientSource {
  customerName?: string | null;
  customerCompany?: string | null;
}

export interface InvoiceRecipientContractText {
  heading: string;
  partyLabel: string;
  partyValue: string;
  recipientLabel: string;
  recipientValue: string;
  note: string;
}

export const INVOICE_RECIPIENT_HEADING = "Fakturamottaker (betalingsadresse)";

/**
 * Presiseringen som gjoer at kontrakten ikke kan leses som at fakturamottakeren
 * er part. Vilkaarsteksten er uroert; dette er ren opplysning i visningen.
 */
export const INVOICE_RECIPIENT_NOTE =
  "Fakturamottakeren er kun betalingsadresse og er ikke part i avtalen. "
  + "Avtaleparten som signerer, er ansvarlig for at fakturaen blir betalt.";

/**
 * Teksten kontrakten viser naar fakturaen skal til et annet selskap. Returnerer
 * null naar feltene er tomme -- da skal kontrakten se ut noeyaktig som i dag.
 */
export function buildInvoiceRecipientContractText(
  input: InvoiceRecipientContractInput | null | undefined,
): InvoiceRecipientContractText | null {
  const recipient = resolveInvoiceRecipient(input);
  if (!recipient) return null;
  const company = clean(input?.customerCompany);
  const name = clean(input?.customerName);
  const partyValue = company ? (name ? `${company} v/ ${name}` : company) : name;
  return {
    heading: INVOICE_RECIPIENT_HEADING,
    partyLabel: "Avtalepart/leietaker",
    partyValue,
    recipientLabel: "Faktura sendes til",
    recipientValue: `${recipient.company}, org.nr ${formatOrgNumber(recipient.orgNumber)}`,
    note: INVOICE_RECIPIENT_NOTE,
  };
}
